<script setup lang="ts">
import { cva } from 'class-variance-authority'
import type { Ref } from 'vue'
import useUserStore from '~/store/user'
import type { ServiceMessageIcon } from '~/interfaces/UI'
import { ServiceMessageType } from '~/interfaces/UI'
import IconGift from '../Icons/IconGift.vue'

export interface Props {
    text: string
    type: ServiceMessageType
    iconName?: ServiceMessageIcon | null
    displayOrderPass?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    text: '',
    type: ServiceMessageType.Info,
    iconName: null,
    displayOrderPass: false
})

const icons = { IconGift }

const displayMoreButton = ref(true)
const displayButton = ref(false)

const serviceMessageText: Ref<InstanceType<typeof HTMLElement> | null> =
    ref(null)

const customStyle = computed(() =>
    cva(
        'flex justify-start items-start gap-2 p-4 mb-5 rounded-lg w-full font-normal text-base leading-[120%] tracking-tighter flex-[4]',
        {
            variants: {
                type: {
                    [ServiceMessageType.Info]: 'bg-sky-100 text-sky-800',
                    [ServiceMessageType.Positive]:
                        'bg-green-100 text-green-800',
                    [ServiceMessageType.Negative]: 'bg-red-100 text-red-800'
                }
            }
        }
    )({ type: props.type })
)

const iconStyle = computed(() =>
    cva('min-w-4 h-4', {
        variants: {
            type: {
                [ServiceMessageType.Info]: 'text-sky-500',
                [ServiceMessageType.Positive]: 'text-green-500',
                [ServiceMessageType.Negative]: 'text-red-500'
            }
        }
    })({ type: props.type })
)

const toggleMoreButton = () => {
    displayMoreButton.value = !displayMoreButton.value
}

const onClickOnOrderPass = () => {
    if (window.location !== window?.parent?.location) {
        ;(window.top as Window).postMessage(
            { action: 'openUrl', url: 'pass/purchase/step1' },
            document.referrer
        )
    } else {
        const newWnd = window.open(
            `https://${useI18n().locale}.chargemap.com/pass/purchase/step1`,
            '_top'
        )

        if (newWnd) {
            newWnd.opener = null
        }
    }
}

onMounted(() => {
    if (serviceMessageText.value) {
        displayButton.value = serviceMessageText.value.scrollHeight > 20
    }
})
</script>

<template>
    <div class="flex flex-col justify-center items-end gap-2">
        <div :class="customStyle">
            <component
                :is="icons[iconName]"
                v-if="iconName"
                :class="iconStyle"
            />
            <div class="overflow-hidden">
                <p
                    ref="serviceMessageText"
                    :class="[
                        displayMoreButton ? 'line-clamp-1' : 'line-clamp-none'
                    ]"
                    v-html="text"
                />
                <ChargemapB2CButton
                    v-if="
                        !useUserStore().userAlreadyHasAPass &&
                        !displayMoreButton &&
                        displayButton &&
                        displayOrderPass
                    "
                    type="tertiary"
                    size="medium"
                    class="mt-4"
                    @click="onClickOnOrderPass"
                >
                    {{ $t('discounted_networks.service_message.order_pass') }}
                </ChargemapB2CButton>
            </div>
            <div
                v-if="displayButton"
                class="cursor-pointer self-end text-fifteen-exploitation font-semibold"
                @click="toggleMoreButton"
            >
                {{
                    displayMoreButton
                        ? $t('show.more_short')
                        : $t('show.less_short')
                }}
            </div>
        </div>
    </div>
</template>
